import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * UI language for the agent console. Independent of the customer's language
 * (the bot already matches the customer; this is what the human agent reads).
 *
 * T9.1 (OP-165) — the console was 100% hardcoded English with no RTL. This
 * store is the single source of truth for the agent's chosen language; the
 * root `dir`/`lang` attributes and every `useT()` consumer subscribe to it.
 */
export type Lang = 'en' | 'he';

/**
 * Default for the primary (Hebrew-speaking) deployment. A single constant so
 * a different default per build is a one-line change. Per-agent choice is
 * persisted to localStorage and overrides this on subsequent loads.
 */
export const DEFAULT_LANG: Lang = 'he';

interface LangState {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggle: () => void;
}

export const useLangStore = create<LangState>()(
  persist(
    (set, get) => ({
      lang: DEFAULT_LANG,
      setLang: (lang) => set({ lang }),
      toggle: () => set({ lang: get().lang === 'he' ? 'en' : 'he' }),
    }),
    { name: 'css-agent-lang' }
  )
);

/** `'rtl'` for Hebrew, `'ltr'` otherwise — used for the document `dir`. */
export const dirFor = (lang: Lang): 'rtl' | 'ltr' => (lang === 'he' ? 'rtl' : 'ltr');
